import React, { Component } from 'react'
import { forEach, result } from 'libraries/lodash'
import escapeStringRegexp from 'escape-string-regexp'
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed'
import loadScript from 'scriptjs'
import findAndReplaceDOMText from 'helpers/domtextnew'
import colorhash from 'helpers/colorhash'

const base = document.querySelector('base')
const baseUrl = base ? base.href : ''

loadScript([baseUrl + 'pdfjs_pdf.min.js'], 'pdfjsmain', () => {
  window.PDFJS = window.PDFJS || window.pdfjsDistBuildPdf || window.pdfjsLib
  loadScript([baseUrl + 'pdfjs_pdf_viewer.js'], () => {
    window.pdfjsDistWebPdfViewer && Object.assign(window.PDFJS, window.pdfjsDistWebPdfViewer)
    window.pdfjsViewer && Object.assign(window.PDFJS, window.pdfjsViewer)
    window.PDFJS.workerSrc = baseUrl + 'pdfjs_pdf.worker.min.js'
    window.PDFJS.GlobalWorkerOptions && (window.PDFJS.GlobalWorkerOptions.workerSrc = baseUrl + 'pdfjs_pdf.worker.min.js')
  })
})

export default class PdfViewer extends Component {
  state = {
    findBarQuery: '',
    findBar: true
  }

  container = null
  hasScrolled = false

  componentDidMount () {
    try {
      const container = document.getElementById('pdfViewer')
      this.pdfViewer = new PDFJS.PDFViewer({
        container,
        enhanceTextSelection: true
      })
      this.createFindController()
      addEventListener('textlayerrendered', this.handlePageRendered)
      addEventListener('DOMNodeRemoved', this.handlePageRemoved)
      this.setPdf({ data: this.props.pdf })
      addEventListener('pagesinit', this.handlePagesInit)
      this.commandSubscription = this.props.$commands.subscribe(this.handleSearch)
    } catch (e) {
      console.error(e)
    }
  }

  createFindController () {
    this.pdfFindController = new PDFJS.PDFFindController({ pdfViewer: this.pdfViewer })
    const nextMatch = this.pdfFindController.nextMatch
    this.pdfFindController.nextMatch = () => {
      nextMatch.call(this.pdfFindController)
      if (!this.state.findBar) {
        this.pdfFindController.active = false
      }
      const element = document.querySelector('.textLayer .highlight.selected')
      if (element) {
        scrollIntoViewIfNeeded(element, true)
      }
    }
    this.pdfViewer.setFindController(this.pdfFindController)
  }

  componentWillUnmount () {
    try {
      clearInterval(this.interval)
      removeEventListener('textlayerrendered', this.handlePageRendered) // eslint-disable-line
      removeEventListener('DOMNodeRemoved', this.handlePageRemoved) // eslint-disable-line
      removeEventListener('pagesinit', this.handlePagesInit) // eslint-disable-line
      this.commandSubscription.unsubscribe()
    } catch (e) {
      console.error(e)
    }
  }

  componentDidUpdate (prevProps) {
    if (prevProps.textSearch !== this.props.textSearch) {
      this.handleSearch()
    }
    if (prevProps.currentScale !== this.props.currentScale) {
      this.pdfViewer.currentScaleValue = this.props.currentScale
    }
  }

  setPdf (pdf) {
    if (pdf && pdf.data) {
      PDFJS.getDocument(pdf.data)
        .then(pdf => this.pdfViewer.setDocument(pdf))
    }
  }

  revealParam = (node) => {
    if (!node || node.className !== 'highlight') {
      return
    }

    this.hasScrolled = this.scrollToElement(node)
  }

  handlePageRendered = (e) => {
    const index = e.detail.pageNumber
    this.updateMarks(e.target, index, this.props.parameters)
  }

  handlePageRemoved = (e) => {
  }

  handlePagesInit = () => {
    this.pdfViewer.currentScaleValue = 'auto'
    const currentScale = this.pdfViewer.currentScale
    const { setMinScale, setCurrentScale } = this.props
    setMinScale(currentScale)
    setCurrentScale(currentScale)
    if (this.scrollToParamPage !== null) {
      this.scrollToElement(this.getPageElement(this.scrollToParamPage))
    }
  }

  get scrollToParam () {
    return result(this.props.parameters, this.props.scrollToParam, null)
  }

  get scrollToParamPage () {
    const param = this.scrollToParam
    const path = this.props.optionalPath?.substring(1) // remove leading .

    if (param && path) {
      if (param.type === 'MultiSelectParameterValue') { // handle getting page number for MultiSelectParameterValue
        return result(param, `${path}.descriptor.context.pageNumber`)
      }
      return result(param, [path, 'descriptor', 'context', 'pageNumber'])
    }

    if (param) {
      return result(param, 'descriptor.context.pageNumber') ||
        result(param, 'values[0].descriptor.context.pageNumber') ||
        1
    }

    return null
  }

  getPageElement (pageNumber) {
    return document.querySelector(`#pdfViewer .page[data-page-number="${pageNumber}"]`)
  }

  handleSearch = (findNext = true) => {
    const { textSearch: query } = this.props
    const findPrevious = !findNext
    if (query !== this.state.findBarPrevQuery) {
      this.pdfFindController.executeCommand('find', {
        query,
        findPrevious,
        phraseSearch: true
      })
      this.setState({ findBarPrevQuery: query })
    } else {
      this.pdfFindController.executeCommand('findagain', {
        query,
        findPrevious,
        phraseSearch: true
      })
    }
  }

  updateMarks (node, pageNumber, params) {
    if (this.scrollToParam !== null) {
      this.revealParam(
        this.updateMarksForParam(
          node,
          pageNumber
        )(
          this.scrollToParam,
          this.props.scrollToParam
        )
      )
    } else {
      forEach(
        params,
        (param, name) =>
          this.updateMarksForParam(node, pageNumber)(param, name)
      )
    }
  }

  updateMarksForParam = (node, pageNumber) => (param, name) => {
    if (!param) {
      return
    }
    const updateMarksForParam = this.updateMarksForParam(node, pageNumber)
    updateMarksForParam(param.minValue, name + '.minValue')
    updateMarksForParam(param.maxValue, name + '.maxValue')

    forEach(
      param.values || [],
      (param, i) =>
        updateMarksForParam(
          param,
          name + `.values[${i}]`
        )
    )

    const paramMark = param.mark || param.descriptor.context
    if (!paramMark) {
      return
    }

    let value = paramMark
    if (typeof paramMark !== 'string') {
      if (paramMark.pageNumber !== pageNumber) {
        return
      }
      value = paramMark.value
    }

    const regex = new RegExp(
      escapeStringRegexp(value).replace(/\s+/g, '\\s*'),
      'g'
    )
    const matches = []
    const group = result(param, 'config.group', '')
    const title = result(param, 'config.description', '')
    const color = this.scrollToParam ? '#FF0000' : param.color || colorhash(param.descriptor.name)

    findAndReplaceDOMText(
      node,
      {
        find: regex,
        replace: (portion, match) => {
          if (match.index === paramMark.pageOccurrence) {
            const span = document.createElement('span')
            span.innerHTML = portion.text
            span.className = 'highlight'
            span.title = `${group}: ${title}`
            span.style.backgroundColor = color
            matches.push(span)
            return span
          } else {
            return portion.text
          }
        }
      }
    )

    return matches[0] || node
  }

  scrollToElement (pageElement) { // element is the page container
    if (!pageElement || this.hasScrolled) {
      return false
    }
    // scroll to the page
    scrollIntoViewIfNeeded(pageElement, true)
    // wait for the page content to load
    return this.waitForHighLightedElement(pageElement).then((highlightedElement) => {
      // found highlighted element and scrolling it to the center of the view
      if (highlightedElement) {
        scrollIntoViewIfNeeded(highlightedElement, {
          centerIfNeeded: true
        })
      }
    }).finally(() => {
      // no highlighted text found just the page scroll
      return true
    })
  }

  waitForHighLightedElement (targetNode = document, stopAfter = 3000) {
    const selector = 'span.highlight'
    return new Promise(resolve => {
      setTimeout(() => { // stop looking for the element after the provide time
        resolve(null)
        observer.disconnect()
      }, stopAfter)

      if (targetNode.querySelector(selector)) {
        return resolve(targetNode.querySelector(selector))
      }

      const observer = new MutationObserver(mutations => {
        if (targetNode.querySelector(selector)) {
          resolve(targetNode.querySelector(selector))
          observer.disconnect()
        }
      })
      observer.observe(targetNode, {
        childList: true,
        subtree: true
      })
    })
  }

  render () {
    return (
      <div id='pdfViewer' ref={container => (this.container = container)}>
        <div className='pdfViewer' />
      </div>
    )
  }
}
